import { apiV2, errorHandler } from "./index";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  API_ERROR,
  FETCH_ALL_SESSIONS,
  FETCH_ALL_SESSIONS_START,
  FETCH_SESSIONS_PAGE_START,
  FETCH_SESSIONS_PAGE_SUCCESS,
  FETCH_SESSIONS_PAGE_FAILURE,
  FETCH_SESSIONS_GROUP_START,
  FETCH_SESSIONS_GROUP_SUCCESS,
  FETCH_SESSIONS_GROUP_FAILURE,
} from "../constants/ActionTypes";

export function fetchAllSessions() {
  return function (dispatch) {
    dispatch({
      type: FETCH_ALL_SESSIONS_START,
    });
    apiV2
      .get("/sessions")
      .then(response => {
        dispatch({
          type: FETCH_ALL_SESSIONS,
          payload: response.data.objects,
        });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

/**
 * Paginated sessions list — calls GET /sessions with the new CMS API
 * paginated-mode query params. Returns the response envelope:
 *
 *   { data: [SessionPaginatedItem…], page, per_page, page_count,
 *     has_more, total_count }
 *
 * Spec: cms-api-openapi.yaml `/sessions` (Backward-compatible mode triggers
 * if NONE of these params are present, so we always pass `page` + `per_page`
 * to guarantee paginated mode and one-row-per-session shape).
 *
 * Accepted params (all optional except page/per_page which we default):
 *   page         — 1-indexed page number (default 1)
 *   per_page     — page size, max 200 (default 50)
 *   sort         — created_at | session_name | author_name | parent_name
 *   dir          — asc | desc
 *   q            — case-insensitive substring across name + parent titles
 *   active       — '0' | '1'
 *   recommended  — '0' | '1'
 *   duplicate    — '0' | '1'
 *   free         — '0' | '1'
 *   parent_type  — category | subcategory | series | challenge |
 *                  audiobook | unparented
 *   author_id    — integer
 *
 * Empty / undefined values are dropped so we don't send `?q=` etc.
 */
export function fetchSessionsPage(params = {}) {
  return function (dispatch) {
    dispatch({ type: FETCH_SESSIONS_PAGE_START, payload: params });

    // Build the query object, dropping null / undefined / '' entries so
    // the backend's "any paginated param triggers paginated mode" rule
    // doesn't get confused by empty strings.
    const query = {
      page: params.page || 1,
      per_page: params.per_page || 50,
    };
    [
      'sort', 'dir', 'q', 'active', 'recommended',
      'duplicate', 'free', 'parent_type', 'author_id',
    ].forEach((k) => {
      const v = params[k];
      if (v === null || v === undefined || v === '') return;
      query[k] = v;
    });

    return apiV2
      .get('/sessions', { params: query })
      .then((response) => {
        dispatch({
          type: FETCH_SESSIONS_PAGE_SUCCESS,
          payload: response.data,
        });
        return response.data;
      })
      .catch((error) => {
        dispatch({
          type: FETCH_SESSIONS_PAGE_FAILURE,
          payload: error.response?.data?.message || 'Failed to load sessions',
        });
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function fetchOneSession(session_id) {
  return function (dispatch) {
    return apiV2
      .get(`/sessions/${session_id}`)
      .then(response => {
        // APIv2 returns a single SessionEditResource object.
        // Component expects response.data.objects[0], so wrap accordingly.
        return {
          ...response,
          data: {
            objects: [response.data],
          },
        };
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function addEditSession(options) {
  return function (dispatch) {
    // Parse the stringified blobs from the component
    const sessionData =
      typeof options.session_json === "string" ? JSON.parse(options.session_json) : options.session_json;

    const timestamps =
      typeof options.timestamps_json === "string" ? JSON.parse(options.timestamps_json) : options.timestamps_json;

    const isEdit = !!sessionData.session_id;

    // Build flat payload with explicit field picks matching APIv2 validation
    const payload = {
      session_name: sessionData.session_name,
      session_description: sessionData.session_description,
      session_is_active: sessionData.session_is_active,
      session_allow_rating: sessionData.session_allow_rating,
      session_is_new: sessionData.session_is_new,
      session_is_dark_mode: sessionData.session_is_dark_mode,
      no_avatar: sessionData.no_avatar,
      session_image: sessionData.session_image,
      session_author_id: sessionData.session_author_id,
      session_is_free: sessionData.session_is_free,
      session_is_recommended: sessionData.session_is_recommended,
      session_is_chapter: sessionData.session_is_chapter,
      session_skip_captions: sessionData.session_skip_captions,
      session_text_page_url: sessionData.session_text_page_url,
      session_text_page_ios_url: sessionData.session_text_page_ios_url,
      session_text_image_url: sessionData.session_text_image_url,
      session_music_url: sessionData.session_music_url,
      // session_bgm_id pairs with session_music_url — the BGM dropdown sets
      // both. Without sending it, APIv2 falls back to the default BGM (id 1)
      // on every save, resetting the admin's selection.
      session_bgm_id: sessionData.session_bgm_id,
      is_bgm_locked: sessionData.is_bgm_locked,
      session_main_url: sessionData.session_main_url,
      session_length: sessionData.session_length,
      session_length_in_sec: sessionData.session_length_in_sec,
      session_content_start_timestamp: sessionData.session_content_start_timestamp,
      session_rating_start: sessionData.session_rating_start,
      session_rating_end: sessionData.session_rating_end,
      session_bg_timestamps: Array.isArray(timestamps) ? timestamps : [],
      category_id: options.category_id || 0,
      subcategory_id: options.subcategory_id || 0,
      series_id: options.series_id || 0,
      challenge_id: options.challenge_id || 0,
    };

    const request = isEdit
      ? apiV2.put(`/sessions/${sessionData.session_id}`, payload)
      : apiV2.post("/sessions", payload);

    return request
      .then(response => {
        return { payload: response.data || true };
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function duplicateSession(sessionId) {
  return function (dispatch) {
    return apiV2
      .post(`/sessions/${sessionId}/duplicate`)
      .then(response => {
        // Returns the newly created session via SessionEditResource
        return response.data;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function deleteSession(session_id, onSuccess) {
  return function (dispatch) {
    apiV2
      .delete(`/sessions/${session_id}`)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Session Deleted!",
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function clearSessions() {
  return function (dispatch) {
    dispatch({
      type: FETCH_ALL_SESSIONS,
      payload: null,
    });
  };
}

/**
 * Fetch one bucket of the grouped-view list. Hits the same paginated
 * `/sessions` endpoint with `parent_type=…` so the API returns only
 * sessions whose primary parent is of that type.
 *
 *   fetchSessionsGroup('category',    { per_page: 25 })
 *   fetchSessionsGroup('subcategory', { per_page: 25 })
 *   fetchSessionsGroup('audiobook',   { per_page: 25 })
 *   fetchSessionsGroup('series',      { per_page: 25 })
 *   fetchSessionsGroup('challenge',   { per_page: 25 })
 *   fetchSessionsGroup('unparented',  { per_page: 25 })
 *
 * Each group is tracked independently in `state.sessions.grouped[type]`,
 * so the reducer keeps the buckets separate and a refresh on one doesn't
 * blow away the others.
 *
 * Accepts the same query params as `fetchSessionsPage` (page, per_page,
 * sort, dir, q, active, recommended, duplicate, free, author_id) so
 * filters / search can apply across groups in the future.
 */
export function fetchSessionsGroup(parent_type, params = {}) {
  return function (dispatch) {
    dispatch({ type: FETCH_SESSIONS_GROUP_START, payload: { parent_type } });

    const query = {
      parent_type,
      page: params.page || 1,
      per_page: params.per_page || 25,
      sort: params.sort || 'created_at',
      dir:  params.dir  || 'desc',
    };
    ['q', 'active', 'recommended', 'duplicate', 'free', 'author_id'].forEach((k) => {
      const v = params[k];
      if (v === null || v === undefined || v === '') return;
      query[k] = v;
    });

    return apiV2
      .get('/sessions', { params: query })
      .then((response) => {
        dispatch({
          type: FETCH_SESSIONS_GROUP_SUCCESS,
          payload: { parent_type, response: response.data },
        });
        return response.data;
      })
      .catch((error) => {
        dispatch({
          type: FETCH_SESSIONS_GROUP_FAILURE,
          payload: {
            parent_type,
            error: error.response?.data?.message || 'Failed to load sessions',
          },
        });
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}
